import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, FileText, Users, User,
  BookOpen, CalendarCog, CalendarDays, ChevronRight,
  Sparkles, ChevronLeft, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse, role }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigationItems = {
    student: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/student/dashboard', color: 'from-uzima-maroon-500 to-uzima-maroon-600' },
      { icon: Calendar, label: 'Appointments', path: '/student/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/student/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: BookOpen, label: 'Resources', path: '/student/resources', badge: 'New', color: 'from-orange-500 to-red-500' },
      { icon: User, label: 'Profile', path: '/student/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    staff: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/staff/dashboard', color: 'from-uzima-maroon-500 to-uzima-maroon-600' },
      { icon: Calendar, label: 'Appointments', path: '/staff/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/staff/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: BookOpen, label: 'Resources', path: '/staff/resources', badge: 'New', color: 'from-orange-500 to-red-500' },
      { icon: User, label: 'Profile', path: '/staff/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    counselor: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/counselor/dashboard', color: 'from-uzima-maroon-500 to-uzima-maroon-600' },
      { icon: Calendar, label: 'Appointments', path: '/counselor/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/counselor/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: BookOpen, label: 'Resources', path: '/counselor/resources', badge: 'New', color: 'from-orange-500 to-red-500' },
      { icon: User, label: 'Profile', path: '/counselor/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    admin: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', color: 'from-uzima-maroon-500 to-uzima-maroon-600' },
      { icon: Users, label: 'Users', path: '/admin/users/manage', color: 'from-violet-500 to-purple-500' },
      { icon: Calendar, label: 'Appointments', path: '/admin/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: CalendarDays, label: 'Events', path: '/admin/calendar-events', color: 'from-amber-500 to-orange-500' },
      { icon: BookOpen, label: 'Resources', path: '/admin/resources', badge: 'New', color: 'from-orange-500 to-red-500' },
      { icon: User, label: 'Profile', path: '/admin/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    receptionist: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/receptionist/dashboard', color: 'from-uzima-maroon-500 to-uzima-maroon-600' },
      { icon: Users, label: 'Users', path: '/receptionist/users/manage', color: 'from-violet-500 to-purple-500' },
      { icon: Calendar, label: 'Appointments', path: '/receptionist/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: CalendarDays, label: 'Events', path: '/receptionist/calendar-events', color: 'from-amber-500 to-orange-500' },
      { icon: CalendarCog, label: 'Schedules', path: '/receptionist/schedules', color: 'from-teal-500 to-cyan-500' },
      { icon: User, label: 'Profile', path: '/receptionist/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    hod: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/hod/dashboard', color: 'from-uzima-maroon-500 to-uzima-maroon-600' },
      { icon: Calendar, label: 'Appointments', path: '/hod/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/hod/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: User, label: 'Profile', path: '/hod/profile', color: 'from-indigo-500 to-purple-500' },
    ],
  };

  const menuItems = navigationItems[role] || [];

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden transition-all duration-300 animate-in fade-in"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-[70px] left-0 bottom-0 z-50
          ${isCollapsed ? 'w-20' : 'w-72'}
          bg-white/80 backdrop-blur-xl border-r border-gray-200/50
          shadow-2xl shadow-black/5
          transform transition-all duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:z-30
          overflow-hidden
        `}
      >
        {/* Gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-uzima-maroon-50/50 via-transparent to-uzima-white-50/50 pointer-events-none" />

        <div className="relative h-full flex flex-col">
          {/* Branding and Collapse Toggle - Desktop Only */}
          <div className="hidden lg:flex items-center justify-between p-3 border-b border-gray-200/50">
            {!isCollapsed ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-lg p-1">
                  <img
                    src="/uzimalogo.jpg"
                    alt="Uzima Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.src = "/uzimalogo.jpg";
                    }}
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 bg-clip-text text-transparent leading-tight">
                    Uzima
                  </span>
                  <span className="text-[0.65rem] font-semibold text-orange-500 leading-tight">
                    Counseling
                  </span>
                </div>
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-lg mx-auto p-1">
                <img
                  src="/uzimalogo.jpg"
                  alt="Uzima Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.src = "/uzimalogo.jpg";
                  }}
                />
              </div>
            )}

            {!isCollapsed && (
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-all duration-200"
                title="Collapse sidebar"
              >
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>

          {/* Expand button when collapsed */}
          {isCollapsed && (
            <div className="hidden lg:flex items-center justify-center p-2 border-b border-gray-200/50">
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-all duration-200"
                title="Expand sidebar"
              >
                <PanelLeftOpen size={18} />
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-300/50 scrollbar-track-transparent hover:scrollbar-thumb-gray-400/50">
            {menuItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;

              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`
                    group relative w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'gap-2 px-3'} py-2.5 rounded-xl
                    font-medium text-sm transition-all duration-300 ease-out
                    ${isActive
                      ? 'text-white shadow-lg shadow-blue-500/25'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-white/60'
                    }
                  `}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                  title={isCollapsed ? item.label : ''}
                >
                  {/* Active background gradient */}
                  {isActive && (
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-2xl transition-all duration-300`} />
                  )}

                  {/* Hover effect */}
                  {!isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  )}

                  {/* Icon container */}
                  <div className={`
                    relative flex items-center justify-center ${isCollapsed ? 'w-8 h-8' : 'w-9 h-9'} rounded-lg
                    transition-all duration-300 ease-out
                    ${isActive
                      ? 'bg-white/20 shadow-lg'
                      : 'bg-gray-100 group-hover:bg-white group-hover:scale-110'
                    }
                  `}>
                    <Icon
                      className={`
                        ${isCollapsed ? 'w-4 h-4' : 'w-[18px] h-[18px]'} transition-all duration-300
                        ${isActive
                          ? 'text-white'
                          : 'text-gray-600 group-hover:text-gray-900 group-hover:scale-110'
                        }
                      `}
                      strokeWidth={isActive ? 2.5 : 2}
                    />
                  </div>

                  {/* Label */}
                  {!isCollapsed && (
                    <span className="relative flex-1 text-left font-semibold">
                      {item.label}
                    </span>
                  )}

                  {/* Badge */}
                  {!isCollapsed && item.badge && (
                    <span className="relative flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold text-white bg-gradient-to-r from-orange-500 to-pink-500 rounded-full uppercase tracking-wider shadow-lg shadow-orange-500/30 animate-pulse">
                      <Sparkles className="w-2.5 h-2.5" />
                      {item.badge}
                    </span>
                  )}

                  {/* Arrow indicator for active */}
                  {!isCollapsed && isActive && (
                    <ChevronRight
                      className="relative w-4 h-4 text-white animate-in slide-in-from-left-2 duration-300"
                      strokeWidth={3}
                    />
                  )}

                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 overflow-hidden rounded-2xl">
                    <div className="absolute -inset-full top-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 group-hover:animate-shine" />
                  </div>
                </button>
              );
            })}
          </nav>

          {/* Footer decoration */}
          <div className="relative p-3 border-t border-gray-200/50">
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'} text-xs text-gray-500`}>
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-lg shadow-green-500/50" />
              {!isCollapsed && <span>System Online</span>}
            </div>
          </div>
        </div>
      </aside>

      <style jsx>{`
        @keyframes shine {
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shine {
          animation: shine 1.5s;
        }
      `}</style>
    </>
  );
};

export default Sidebar;
